import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import guid from "guid";

import { logIt } from "@/services/logger";
import { success, toastMsg } from "@/services/variables";
import { getAllRequests } from "@/store/slices/requests";
import OnInitHub from "@/services/signal-R/on-receive-hook";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import Lead_signalr from "@/modules/crm/leads/signalr-constants";
import { getSimilarItemsFrom2Arrays } from "@/services/generic.methods";

import TagOptions from "./tag-options";
import AddTagModal from "./add-tag-modal";
import SelectedTags from "./selected-tags";
import { Chip } from "@mui/material";
import { Cancel } from "@mui/icons-material";

import {
  addNewTagToOptions,
  deleteTagFromOptions,
  getTagColors,
} from "@/store/slices/general/dropdownOptionsSlice";
import TruncateTooltip from "@/components/truncate-with-tooltip";
import { useRouter } from "next/router";

const {
  hubName,
  InvokeMethodNames: { addTag, editTag, deleteTag },
  onReceiveMethodNames: {
    onAddTagResponse,
    onEditTagResponse,
    onDeleteTagResponse,
  },
} = Lead_signalr;

const resultMethods = {
  [onAddTagResponse]: { successOperation: "saved" },
  [onEditTagResponse]: { successOperation: "updated" },
  [onDeleteTagResponse]: { successOperation: "deleted" },
};
let methodKeys = Object.keys(resultMethods);

const filterOptions = createFilterOptions({
  stringify: (option) => option?.name || "",
});

const initialColor = { id: 1, name: "Clay red", code: "#FFD0D6" };

const TagsInput = ({
  title,
  name,
  value,
  handleChange,
  disabled,
  tagOptions = [],
  colorOptions = [],
  savedTags = [],
  size = "small",
  allowAdd = true,
  allowDelete = true,
  showChipOnFieldOnly = false,
}) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [saveTags, setSavedTags] = useState([]);
  const [tagsData, setTagsData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [deleteTagId, setDeleteTagId] = useState(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState(null);
  const tagColors = useSelector(getTagColors);
  const tagColorOptions = colorOptions?.length ? colorOptions : tagColors;
  const dispatch = useDispatch();
  // listeners vars
  const requests = useSelector(getAllRequests);
  const { clearStateByMethods } = OnInitHub();
  const { statuses, RTC } = useSignalRClient({ hubName: hubName });
  const leadhub = RTC[hubName];
  const { query } = useRouter();

  const savedTagIdsKey = (savedTags || []).map((tag) => tag?.tagId).join(",");

  // Map server-saved lead tags onto option objects and write them into saveTags.
  // Keyed by tag ids so a parent re-render does not wipe in-progress removals.
  useEffect(() => {
    if (!savedTagIdsKey || !tagOptions?.length) return;

    const matched = getSimilarItemsFrom2Arrays(
      tagOptions,
      "id",
      savedTags,
      "tagId",
      "int"
    );

    if (showChipOnFieldOnly) {
      setSelectedTags(matched);
    } else {
      setSavedTags(matched);
    }
  }, [savedTagIdsKey, tagOptions?.length, showChipOnFieldOnly]);

  // Restore selected tags from external value (e.g. draft/redux state) when
  // the component mounts with a pre-filled value but empty internal state.
  // This covers the case where the user navigates away and comes back.
  useEffect(() => {
    if (!Array.isArray(value) || value.length === 0 || selectedTags.length > 0) {
      return;
    }

    const savedTagIds = new Set((savedTags || []).map((t) => t.tagId));
    const fromNew = savedTagIds.size
      ? value.filter((t) => !savedTagIds.has(t.id))
      : value;

    if (fromNew.length) setSelectedTags(fromNew);
  }, [value?.length, savedTags?.length]);

  useEffect(() => {
    if (disabled) {
      setSelectedTags([]);
    }
  }, [disabled]);

  useEffect(() => {
    // response methods signal_R listeners for lead tag
    let findedKey = methodKeys.find((key) => key in requests && requests[key]);
    if (findedKey) {
      // this will only run if any request with same method have data in it
      // no need for reorder listener
      let response = requests[findedKey];
      let successOperation = resultMethods[findedKey]?.successOperation;
      if (allowAdd) {
        responseHandler(response, successOperation);
      }
      clearStateByMethods(methodKeys);
    }
  }, [
    requests[onAddTagResponse],
    requests[onEditTagResponse],
    requests[onDeleteTagResponse],
  ]);

  const responseHandler = (response, successOperation = "") => {
    console.log("operattion", successOperation, response);
    if (response.isSuccess) {
      let tag = response?.tag || null;
      onSuccess(successOperation, tag);
    } else {
      let msg = response.errors[0];
      onError(msg);
    }
  };

  const onSuccess = (operation, newTag) => {
    // setSubmitting(Enums?.RequestStatus.initial);
    toast.success(success.saveSuccess("Lead Tag", operation));
    if (newTag) {
      let tempTags = JSON.parse(JSON.stringify(tagsData));
      let matchingColor = tagColorOptions.find(
        (x) => x.id === newTag.tagColorId
      );
      newTag = {
        ...newTag,
        code: matchingColor.code,
        colorName: matchingColor.name,
      };
      dispatch(addNewTagToOptions(newTag));
      setTagsData([...tempTags, newTag]);
      setSelectedTags([...selectedTags, newTag]);
      handleChange([...saveTags, ...selectedTags, newTag]);
    }
    if (operation === "deleted") {
      onDeleteSuccess();
    }
    setProcessing(false);
  };

  const onError = (errorMsg) => {
    // setSubmitting(Enums?.RequestStatus.inital);
    toast.error(toastMsg.errors.customError(errorMsg));
    setProcessing(false);
  };

  const onDeleteSuccess = () => {
    let tempTags = JSON.parse(JSON.stringify(tagsData));
    tempTags = tempTags.filter((item) => item?.id !== +deleteTagId);
    setTagsData(tempTags);
    dispatch(deleteTagFromOptions({ id: deleteTagId }));
    setDeleteTagId(null);
  };

  useEffect(() => {
    if (tagOptions && tagOptions?.length) saveTagOptions();
  }, [tagOptions.length]);

  const saveTagOptions = () => {
    console.log("tagOptions", tagOptions);
    let options = JSON.parse(JSON.stringify(tagOptions));
    setTagsData(options);
  };

  const onChangeSubmit = async () => {
    let formData = { name: tagName, tagColorId: tagColor };
    console.log("formData--", formData);
    onSave(formData);
  };

  const handleClick = (option) => {
    let tagName = option.name;
    let tagId = option.id;
    if (option.color === "") {
      tagName = tagName
        .replace('Add "', "")
        .replace('"', "")
        .replace(/\s+/g, "_");
      setTagName(tagName);
      setOpenModal(true);
    } else {
      const selectedTag = tagsData.find((tag) => tag.id === tagId);
      const newTag = {
        ...selectedTag,
        id: selectedTag?.id,
        name: tagName,
      };
      if (selectedTags.some((tag) => tag.id === tagId)) {
        handleTagDelete(tagId);
      } else {
        setSelectedTags([...selectedTags, newTag]);
        handleChange([...saveTags, ...selectedTags, newTag]);
      }
    }
    setInputValue("");
  };

  const handleTagDelete = (tagId, saved = false) => {
    if (saved) {
      let filteredSelectedTags = saveTags.filter((tag) => tag.id !== tagId);
      setSavedTags(filteredSelectedTags);
      handleChange([...selectedTags, ...filteredSelectedTags]);
    } else {
      let filteredSelectedTags = selectedTags.filter((tag) => tag.id !== tagId);
      setSelectedTags(filteredSelectedTags);
      handleChange([...saveTags, ...filteredSelectedTags]);
    }
  };

  const onSave = (value) => {
    if (statuses[hubName] && leadhub) {
      let methodName = addTag;
      let payload = createPayload(value);
      console.log("payload ::", payload);
      onSend(methodName, payload);
    }
  };

  const createPayload = (data) => {
    let payload = {
      tag: {
        ...data,
      },
      requestId: guid.raw(),
    };
    return payload;
  };

  const onSend = (methodName, payload) => {
    setLoading(true);
    leadhub.invoke(methodName, payload).then((res) => {
      console.log("res", res);
      resetFormState();
      setOpenModal(false);
      setLoading(false);
      logIt("log", "on add lead tag callback::", res);
    });
  };

  const resetFormState = () => {
    setTagName("");
    setTagColor(null);
  };

  const deleteHandler = (e, tag) => {
    e.stopPropagation();
    const leadId = Number(query?.id);
    console.log("deleteHandler", tag);
    let payload = {
      id: tag.id,
      requestId: guid.raw(),
      ...(leadId ? { leadId: leadId } : {}),
    };
    setDeleteTagId(tag.id);
    setProcessing(true);
    leadhub.invoke(deleteTag, payload).then((res) => {
      console.log("delete res", res);
      logIt("log", "on delete lead tag callback::", res);
    });
  };
  const handleKeyDown = (event, newInputValue) => {
    if (event?.type === "keydown" && event.key === "Enter") {
      event.preventDefault();
      if (highlightedOption) handleClick(highlightedOption);
      else if (inputValue) {
        let filteredTags = tagsData.filter(
          (x) => !selectedTags.some((s) => s.id === x.id)
        );
        let firstMatchingTag = filteredTags.find(
          (tag) => tag.name.indexOf(inputValue) > -1
        );
        if (firstMatchingTag) handleClick(firstMatchingTag);
        else if (allowAdd && !selectedTags.some((x) => x.name === inputValue)) {
          handleClick({
            name: `Add "${inputValue}"`,
            color: "",
            id: 0,
          });
        }
      }
    } else {
      if (event !== null)
        if (newInputValue !== "undefined") setInputValue(newInputValue);
    }
  };
  return (
    <div>
      <Autocomplete
        id="tags-autocomplete"
        freeSolo
        multiple
        options={processing ? [{}] : tagsData}
        inputValue={inputValue}
        size={size}
        disabled={disabled}
        readOnly={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            value={inputValue}
            sx={{
              background: disabled
                ? "var(--palette-disabled_button)"
                : "var(--palette-grid_table)",
            }}
            InputLabelProps={{
              style: {
                color: disabled
                  ? "var(--palette-disabled_button_text)"
                  : "var(--palette-grey_text)",
              },
            }}
            InputProps={{
              ...params?.InputProps,
              style: {
                color: "var(--palette-text) !important",
              },
            }}
          />
        )}
        getOptionLabel={(option) => option?.name || ""}
        onInputChange={handleKeyDown}
        onHighlightChange={(event, option) => {
          setHighlightedOption(option);
        }}
        ListboxProps={{
          className: "custom-scroll",
        }}
        filterOptions={(options, params) => {
          const filtered = filterOptions(options, params);

          const { inputValue } = params;
          const isExisting = options.some(
            (option) => option.name === inputValue
          );

          if (allowAdd && inputValue?.trim() !== "" && !isExisting) {
            filtered.push({
              name: `Add "${inputValue}"`,
              color: "",
              id: 0,
            });
          }
          return filtered;
        }}
        sx={{
          "& .MuiInput-root": {
            "&:hover:not(.Mui-disabled, .Mui-error)::before": {
              borderBottom: "2px solid var(--palette-stroke) !important",
            },
            "&.Mui-focused::before": {
              borderBottom: "2px solid var(--palette-stroke) !important",
            },
          },
          "& ,MuiAutocomplete-endAdornment": {
            "& .MuiAutocomplete-clearIndicator": {
              "& svg": {
                color: "var(--palette-text) !important",
                fill: "var(--palette-text) !important",
              },
            },
          },
        }}
        value={selectedTags || [{}]}
        renderTags={(value, getTagProps) =>
          value.map((tag, index) => {
            let filterColor = tagColorOptions.find(
              (color) => color?.id == tag.tagColorId
            );
            return (
              <>
                <Chip
                  key={index}
                  label={<TruncateTooltip text={tag.name} limit={15} />}
                  onDelete={() => handleTagDelete(tag.id)}
                  style={{
                    backgroundColor: filterColor?.code,
                    margin: "4px",
                    color: filterColor?.textCode,
                  }}
                  deleteIcon={
                    <Cancel
                      style={{ fontSize: "20px", color: filterColor?.textCode }}
                    />
                  }
                />
              </>
            );
          })
        }
        renderOption={(props, option, { selected }) =>
          processing ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <TagOptions
              key={option.id}
              props={props}
              option={option}
              selectedTags={[...selectedTags, ...saveTags]}
              handleClick={handleClick}
              handleTagDelete={allowDelete ? deleteHandler : false}
              colorOptions={tagColorOptions}
            />
          )
        }
      />

      {/* selected tags */}
      <SelectedTags
        tags={saveTags}
        handleTagDelete={handleTagDelete}
        disabled={disabled}
        colorOptions={tagColorOptions}
      />

      {/* add new tag modal */}
      <AddTagModal
        openModal={openModal}
        loading={loading}
        tagName={tagName}
        setTagName={setTagName}
        tagColor={tagColor}
        setTagColor={setTagColor}
        colorOptions={tagColorOptions}
        onChangeSubmit={onChangeSubmit}
        handleClose={() => {
          setOpenModal(false);
          resetFormState();
        }}
      />
    </div>
  );
};

export default TagsInput;
